import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ShoppingCart, User, LogOut, Menu, X, ShieldCheck, Bell } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { authAPI, cartAPI, notificationsAPI } from '@/utils/api';
import { toast } from 'sonner';

const Navbar = () => {
  const [user, setUser] = useState(null);
  const [cartCount, setCartCount] = useState(0);
  const [unreadCount, setUnreadCount] = useState(0);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  const checkAuth = async () => {
    try {
      const response = await authAPI.getMe();
      setUser(response.data);
    } catch (error) {
      setUser(null);
    }
  };

  const loadCartCount = async () => {
    try {
      const response = await cartAPI.get();
      setCartCount(response.data.length);
    } catch (error) {
      console.error('Failed to load cart');
    }
  };

  const loadUnreadCount = async () => {
    try {
      const response = await notificationsAPI.getUnreadCount();
      setUnreadCount(response.data.count);
    } catch (error) {
      console.error('Failed to load notifications');
    }
  };

  useEffect(() => {
    checkAuth();
    if (localStorage.getItem('token')) {
      loadCartCount();
      loadUnreadCount();
    }
  }, []);

  const handleLogout = async () => {
    try {
      await authAPI.logout();
      localStorage.removeItem('token');
      setUser(null);
      toast.success('Logged out successfully');
      navigate('/');
    } catch (error) {
      toast.error('Logout failed');
    }
  };

  return (
    <nav className="fixed top-0 w-full z-50 border-b border-white/5 bg-[#09090b]/80 backdrop-blur-xl" data-testid="navbar">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          <Link to="/" className="flex items-center space-x-2" data-testid="logo-link">
            <img 
              src="https://customer-assets.emergentagent.com/job_xtream-store/artifacts/5y14iky3_image-removebg-preview%20%281%29.png" 
              alt="Xtream Store" 
              className="h-10 w-auto"
            />
            <span className="text-xl font-heading font-bold tracking-tight" data-testid="brand-name">Xtream Store</span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link to="/category/streaming" className="text-muted-foreground hover:text-white transition-colors" data-testid="streaming-link">
              Streaming
            </Link>
            
            {user ? (
              <>
                {/* Notifications Bell */}
                <Link to="/profile?tab=notifications" className="relative" data-testid="notifications-link">
                  <Bell className="w-5 h-5 text-muted-foreground hover:text-white transition-colors" />
                  {unreadCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="notification-count">
                      {unreadCount}
                    </span>
                  )}
                </Link>
                
                <Link to="/cart" className="relative" data-testid="cart-link">
                  <ShoppingCart className="w-5 h-5 text-muted-foreground hover:text-white transition-colors" />
                  {cartCount > 0 && (
                    <span className="absolute -top-2 -right-2 bg-primary text-white text-xs rounded-full w-5 h-5 flex items-center justify-center" data-testid="cart-count">
                      {cartCount}
                    </span>
                  )}
                </Link>
                
                <Link to="/profile" data-testid="profile-link">
                  <Button variant="ghost" size="sm" className="gap-2">
                    <User className="w-4 h-4" />
                    {user.name}
                  </Button>
                </Link>
                
                {user.role === 'admin' && (
                  <Link to="/admin" data-testid="admin-link">
                    <Button variant="outline" size="sm" className="gap-2">
                      <ShieldCheck className="w-4 h-4" />
                      Admin
                    </Button>
                  </Link>
                )}
                
                <Button variant="ghost" size="sm" onClick={handleLogout} data-testid="logout-button">
                  <LogOut className="w-4 h-4" />
                </Button>
              </>
            ) : (
              <div className="flex items-center gap-3">
                <Link to="/login" data-testid="login-link">
                  <Button variant="ghost" size="sm">Login</Button>
                </Link>
                <Link to="/signup" data-testid="signup-link">
                  <Button size="sm" className="btn-primary-glow">Sign Up</Button>
                </Link>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X /> : <Menu />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-white/5 bg-[#09090b]/95 backdrop-blur-xl" data-testid="mobile-menu">
          <div className="px-6 py-4 space-y-3">
            <Link to="/category/streaming" className="block py-2" onClick={() => setMobileMenuOpen(false)}>
              Streaming
            </Link>
            {user ? (
              <>
                <Link to="/profile" className="flex items-center justify-between py-2" onClick={() => setMobileMenuOpen(false)}>
                  <span>Profile</span>
                  {unreadCount > 0 && (
                    <span className="bg-primary text-white text-xs rounded-full px-2 py-0.5">
                      {unreadCount} new
                    </span>
                  )}
                </Link>
                <Link to="/cart" className="block py-2" onClick={() => setMobileMenuOpen(false)}>
                  Cart ({cartCount})
                </Link>
                {user.role === 'admin' && (
                  <Link to="/admin" className="block py-2" onClick={() => setMobileMenuOpen(false)}>
                    Admin Panel
                  </Link>
                )}
                <button onClick={() => { handleLogout(); setMobileMenuOpen(false); }} className="block py-2 w-full text-left">
                  Logout
                </button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full">Login</Button>
                </Link>
                <Link to="/signup" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full">Sign Up</Button>
                </Link>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
